import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import {
  AljUserStatus,
  CreateUpdateDueProcessDocumentDto,
  CreateUpdateDueProcessDto,
  CreateUpdateDueProcessPartyDto,
  DueProcessDisputeDocumentDto,
  DueProcessDisputePartyDto,
  DueProcessDto,
} from "../../models/dispute-resolution/due-process-models";

@Injectable({
  providedIn: "root",
})
export class DueProcessService {
  private readonly baseUrl = "/api/dispute-resolution/due-process";
  private readonly partyBaseUrl = `${this.baseUrl}/parties`;
  private readonly documentBaseUrl = `${this.baseUrl}/documents`;

  constructor(private http: HttpClient) {}

  create(request: CreateUpdateDueProcessDto): Observable<DueProcessDto> {
    return this.http.post<DueProcessDto>(`${this.baseUrl}`, request);
  }

  getById(id: string): Observable<DueProcessDto> {
    return this.http.get<DueProcessDto>(`${this.baseUrl}/${id}`);
  }

  update(id: string, request: CreateUpdateDueProcessDto): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/${id}`, request);
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  // Splits this case into its opposite-type counterpart (Standard <-> Expedited) and
  // returns the newly created case.
  split(id: string): Observable<DueProcessDto> {
    return this.http.post<DueProcessDto>(`${this.baseUrl}/${id}/split`, null);
  }

  getAll(): Observable<DueProcessDto[]> {
    return this.http.get<DueProcessDto[]>(`${this.baseUrl}`);
  }

  generateCaseNumber(isExpeditedHearing: boolean): Observable<string> {
    return this.http.get(
      `${this.baseUrl}/generate-case-number/${isExpeditedHearing}`,
      {
        responseType: "text",
      }
    );
  }

  updateCaseStatus(
    disputeCaseId: string,
    workflowStatusChangesId: string
  ): Observable<void> {
    return this.http.put<void>(
      `${this.baseUrl}/update-case-status/${disputeCaseId}/${workflowStatusChangesId}`,
      null
    );
  }

  createParty(
    request: CreateUpdateDueProcessPartyDto
  ): Observable<DueProcessDisputePartyDto> {
    return this.http.post<DueProcessDisputePartyDto>(
      `${this.partyBaseUrl}`,
      request
    );
  }

  updateParty(
    id: string,
    request: CreateUpdateDueProcessPartyDto
  ): Observable<void> {
    return this.http.put<void>(`${this.partyBaseUrl}/${id}`, request);
  }

  // Read-only: checks whether an email already belongs to a user and whether that
  // user holds DPCon, so the add-party UI can decide which ALJ modal to show.
  getAljUserStatus(email: string): Observable<AljUserStatus> {
    return this.http.get<AljUserStatus>(`${this.partyBaseUrl}/aljt-user-status`, {
      params: { email },
    });
  }

  createDocument(
    request: CreateUpdateDueProcessDocumentDto
  ): Observable<DueProcessDisputeDocumentDto> {
    return this.http.post<DueProcessDisputeDocumentDto>(
      `${this.documentBaseUrl}`,
      request
    );
  }

  getDocumentsByDisputeCase(
    disputeCaseId: string
  ): Observable<DueProcessDisputeDocumentDto[]> {
    return this.http.get<DueProcessDisputeDocumentDto[]>(
      `${this.documentBaseUrl}/case/${disputeCaseId}`
    );
  }

  deleteDocument(id: string): Observable<void> {
    return this.http.delete<void>(`${this.documentBaseUrl}/${id}`);
  }

  setDocumentPartyAccess(
    partyId: string,
    docId: string,
    canAccess: boolean
  ): Observable<void> {
    return this.http.put<void>(
      `${this.partyBaseUrl}/${partyId}/document/${docId}/${canAccess}`,
      null
    );
  }
}
